import { useMemo, useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { TextField } from "src/components/FormikComponents/TextField";
import {
  IBidPairing,
  PolicyAction,
  hasPermission,
  IBidPackage,
  IWebsocketBidPkgBuildProgress,
  BidPairingState,
} from "@vmosolutions/oc-interfaces";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
// Import tableData definitions from search categories.
import { ITableData, tableData } from "./BidPairingTable";
import { Grid, Typography, Button, LinearProgress } from "@mui/material"; // Typography, Button
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICrewBid } from "../helpers";
import _ from "lodash";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { updatePackageBidPairingCreationProgress } from "src/redux/actions/Bidding.Actions";

interface IBidPairingViewProps {
  packageId: string;
  path: string[];
  id: string;
  setBidPairingCount: (bidPairings: { pairingId: string; bidPairingId: string }[]) => void; // refresh package if user has removed bid pairings.
  bidPairingProgressStatus: IWebsocketBidPkgBuildProgress | null;
  bidPairingprogressPercentage: number;
}

export interface ITableColumn {
  id: string;
  value: string;
  index: number;
  numeric: boolean;
  label: string;
  type: string;
  width: number;
  visible: boolean;
  filter: boolean;
}

// This response needs improvement as it doesn't return a new dataset forcing a second call to update the table
// Also needs more information about which ones failed to be removed (counts could be array of ids)
interface IDeleteBidPairingResponse {
  bidPackage: IBidPackage;
  bidPairings: { pairingId: string; bidPairingId: string }[];
  crewBids: ICrewBid[];
  bidsDeleted: number;
  deletedCount: number;
  failedToDelete: number;
}

interface IStatusFilterOption {
  label: string;
  value: string;
  color: "primary" | "secondary" | "error" | "inherit" | "initial" | "textPrimary" | "textSecondary";
}
const defaultFilter: IStatusFilterOption = { label: "Show All", value: "all", color: "textPrimary" };
const statusFilterOptions: IStatusFilterOption[] = [
  defaultFilter,
  { label: "Pending", value: BidPairingState.pending, color: "primary" },
  { label: "Not Applicable", value: BidPairingState.notApplicable, color: "secondary" },
  { label: "Error", value: BidPairingState.error, color: "error" },
  { label: "Populated", value: BidPairingState.populated, color: "inherit" },
  // { label: "Status Unknown", value: null, color: "error" },
];

export const BidPairingView = (props: IBidPairingViewProps): JSX.Element => {
  const { packageId, id, path, setBidPairingCount, bidPairingProgressStatus, bidPairingprogressPercentage } = props;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);
  const dispatch = useDispatch();

  // Retrieve and store bid pairings for currently selected package.
  const [bidPairings, setBidPairings] = useState<IBidPairing[]>([]);

  const getBidPairings = useCallback(async () => {
    setSubmitting(true);
    // Reset any progress stored in redux when retrieving updated bid pairings.
    const defaultCreationProgress: Partial<IWebsocketBidPkgBuildProgress> = { bidPkgId: packageId };
    dispatch(updatePackageBidPairingCreationProgress(defaultCreationProgress as IWebsocketBidPkgBuildProgress));
    const response: any = await AmplifyAPIWrapper.Get<any>(`/bidding/package/${packageId}/pairings`, null);
    // console.log("response", response);
    if (Array.isArray(response)) {
      setBidPairings(response);
    }
    setSubmitting(false);
  }, [packageId, dispatch]);

  // Store selected bid pairing ids in state and prompt user to add them to bid pairing.
  const [selectedBidPairingIds, setSelectedBidPairingIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);

  const handleRemoveBidPairings = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    //console.log("payload", values);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Remove Bid Pairings From Package...",
      successMessage: "Bid Pairings Removed From Package Successfully",
      errorMessage: "Error: Unable to Remove Bid Pairings From Package, try a smaller selection set.",
    };
    const response: IDeleteBidPairingResponse = await AmplifyAPIWrapper.Del<any>(
      `/bidding/pairings/${packageId}?force=true`,
      selectedBidPairingIds,
      notifications
    );
    if (response?.deletedCount > 0) {
      setBidPairingCount(Array.isArray(response?.bidPairings) ? response?.bidPairings : []);
      getBidPairings(); // If any bid pairings were successfully deleted, reload table data.
      setSelectedBidPairingIds([]);
    }
    // console.log("response", response);
    setSubmitting(false);
  }, [dispatch, getBidPairings, packageId, selectedBidPairingIds, setBidPairingCount]);

  /**
   * SELECT + FOCUS? events selected in the menu
   * @param events - events to select in timeline
   * @param focusSelected - whether or not to focus on those events (zoom contain)
   * @param dataType - the type of event i.e. pairings, flights etc.
   */
  const handleSelectPairings = useCallback(
    (events: IBidPairing[], focusSelected?: boolean, dataType?: string): void => {
      switch (dataType) {
        case "bidPairings": {
          // console.log("bid pairings", events);
          const addPairingsIds = events?.length ? events?.map((bidPairing) => (bidPairing as any)?._id) : [];
          setSelectedBidPairingIds(addPairingsIds);
          break;
        }
        default:
        // code block
      }
    },
    []
  );

  // Crew verification filters.
  const [shortCodeFilter, setshortCodeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<IStatusFilterOption>(defaultFilter);

  const bidPairingsWithStatus = useMemo(() => {
    if (bidPairingProgressStatus?.bidPairingId) {
      //const updatedBidPairings = [...bidPairings];
      const bpIndex = bidPairings?.findIndex((bp) => bp._id === bidPairingProgressStatus?.bidPairingId);
      // console.log(bpIndex);
      // console.log(bidPairingProgressStatus);
      if (bpIndex > -1) {
        //TODO change this up if the websocket is updated to include the full IBidPairing object as pairing info is missing.
        const updatedBidPairing = {
          ...bidPairings[bpIndex],
          ...bidPairingProgressStatus,
          // updated state of curreny bid pairing creation
          // stateReason: bidPairingProgressStatus?.stateReason, // reason for why the pairing is NA or errored
        };
        //if (updatedBidPairing.state === "populated") {
        //  console.log(updatedBidPairing);
        //  console.log(bidPairings[bpIndex]);
        //}
        bidPairings[bpIndex] = updatedBidPairing;
        return [...bidPairings]; // return spread array here so that table detects change in data.
      }
    }
    return bidPairings;
  }, [bidPairings, bidPairingProgressStatus]);

  // Add crew data to the table data.
  const tableContent = useMemo(() => {
    const filteredBidPairing = bidPairingsWithStatus?.filter((bp) => {
      const matchingShortCode =
        !shortCodeFilter ||
        shortCodeFilter?.length === 0 ||
        bp?.pairing?.displayPairing?.shortId?.toLowerCase().includes(shortCodeFilter?.toLowerCase());
      const matchingStatus = statusFilter.value === "all" || bp.state === statusFilter.value;
      return matchingShortCode && matchingStatus;
    });
    return tableData?.map((td) => ({ ...td, data: filteredBidPairing }));
  }, [bidPairingsWithStatus, shortCodeFilter, statusFilter]);

  const allowedTableData: ITableData[] = useMemo(
    () =>
      tableContent.filter((data: ITableData) => hasPermission(policies, data.permissionResource, PolicyAction.Read)),
    [tableContent, policies]
  );

  const [containerHeight, setContainerHeight] = useState<number>(0);
  useLayoutEffect(() => {
    setTimeout(() => {
      const tableHeaderHeight = 180;
      const container = tableContainerRef?.current?.getBoundingClientRect();
      const tableHeight = container?.height ? Math.round(container?.height) - tableHeaderHeight : 0;
      // console.log(tableHeight);
      setContainerHeight(tableHeight);
    }, 100);
  }, [path, tableContainerRef]);

  const handleClearSelection = useCallback(() => {
    setSelectedBidPairingIds([]);
    if (tableRef?.current?.hasOwnProperty("clearSelectedrows")) {
      tableRef.current.clearSelectedrows();
    }
  }, [tableRef]);

  //if some how you open this window and have no allowed table data
  if (!allowedTableData.length) {
    return null;
  }

  return (
    <Grid container direction="column" style={{ height: "100%" }} ref={tableContainerRef}>
      <FilterSearchTable
        ref={tableRef}
        id={id}
        path={path}
        className="pairing_search_table"
        events={allowedTableData}
        parentId={mosaicPanel}
        uniqueKey={"_id"}
        changeSelectedRows={handleSelectPairings}
        hideFilter={true}
        showDateRangeRRule={true}
        hideFocusSelected={true}
        maxTableHeight={containerHeight}
        defaultRowsPerPage={25}
        optionalControls={
          <Grid container direction="column">
            <Grid container alignItems="center" className="add_pairing_container">
              <Typography variant="caption" color="textPrimary" style={{ margin: "0px 10px" }}>
                ({selectedBidPairingIds?.length > 0 ? selectedBidPairingIds?.length : "No"} bid pairings selected)
              </Typography>
              <Typography variant="caption" color="inherit">
                {selectedBidPairingIds?.length > 0
                  ? "Selected bid pairings will be removed from package."
                  : "Select bid pairings to remove them from bid package."}
              </Typography>
              <Button
                className="refresh_btn"
                variant="contained"
                color="primary"
                onClick={_.debounce(handleRemoveBidPairings, 500)}
                disabled={!selectedBidPairingIds?.length || submitting}
              >
                Remove from Bid Package
              </Button>
              <Button
                className="refresh_btn"
                variant="outlined"
                onClick={() => handleClearSelection()}
                disabled={!selectedBidPairingIds?.length || submitting}
              >
                Clear Selection
              </Button>
              <Button
                className="refresh_btn"
                variant="outlined"
                onClick={_.debounce(getBidPairings, 500)}
                disabled={submitting}
              >
                {bidPairings?.length ? "Refresh Bid Pairings" : "Load Bid Pairings"}
              </Button>
            </Grid>
            <Grid container alignItems="center" style={{ marginLeft: 15, minWidth: 1080 }} wrap="nowrap">
              <Typography variant="caption" color="textPrimary">
                Filter Bid Pairings
              </Typography>
              <TextField
                className="field textfield shortcode_filter"
                name="bid_pairing_shortcode_filter"
                label="Short Code"
                value={shortCodeFilter}
                onChange={(event) => setshortCodeFilter(event?.target?.value ?? null)}
                type="text"
              />
              <SelectField
                className="field selectfield bid_pairing_status_filter"
                name="bid_pairing_status_filter"
                label="Status"
                isMulti={false}
                formatOptionLabel={(option: IStatusFilterOption) => (
                  <span style={{ color: "#00FF00" }}>
                    <Typography variant="caption" color={option.color}>
                      {option.label}
                    </Typography>
                  </span>
                )}
                options={statusFilterOptions}
                value={statusFilter}
                onChange={setStatusFilter}
              />
              <Button
                className="refresh_btn"
                variant="outlined"
                onClick={() => {
                  setshortCodeFilter(null);
                  setStatusFilter(defaultFilter);
                }}
              >
                Reset Filter
              </Button>
            </Grid>
            <Grid item className="loading_bar" style={{ width: "100%", margin: "10px 0px 0px -10px" }}>
              <LinearProgress variant="determinate" value={bidPairingprogressPercentage} />
            </Grid>
          </Grid>
        }
        // hideSelectionCheckboxes={true}
      />
    </Grid>
  );
};
